import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import {
  useGetRoomsQuery,
  useAddRoomMutation,
  useEditRoomMutation,
  useDeleteRoomMutation
} from '../../../redux/apis/ConferenceRoomApi';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { notification } from '../../../utils/notification';
import { TOAST_TYPE } from '../../../utils/constant/errorMessage';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const ROOM_STATUS = { AVAILABLE: 'Available', MAINTENANCE: 'Maintenance' };

const COLOR_SWATCHES = [
  '#484c7f',
  '#0ea5e9',
  '#16a34a',
  '#f59e0b',
  '#ec4899',
  '#8b5cf6',
  '#ef4444'
];

const emptyRoom = {
  name: '',
  capacity: '',
  location: '',
  color: '#484c7f',
  amenities: ''
};

const ManageRoomsModal = ({ show, onHide }) => {
  const { data: roomsData, refetch } = useGetRoomsQuery({});
  const rooms = roomsData?.data || [];

  const [addRoom] = useAddRoomMutation();
  const [editRoom] = useEditRoomMutation();
  const [deleteRoom] = useDeleteRoomMutation();

  const [editing, setEditing] = useState(null); // room being edited, or null for new
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, id: '' });
  const [deleting, setDeleting] = useState(false);

  const {
    register,
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues: emptyRoom });

  const selectedColor = watch('color');

  const openNew = () => {
    setEditing(null);
    reset(emptyRoom);
    setShowForm(true);
  };

  const openEdit = (room) => {
    setEditing(room);
    reset({
      name: room.name || '',
      capacity: room.capacity || '',
      location: room.location || '',
      color: room.color || '#484c7f',
      amenities: (room.amenities || []).join(', ')
    });
    setShowForm(true);
  };

  const onSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = {
        name: values.name,
        capacity: Number(values.capacity),
        location: values.location || undefined,
        color: values.color || undefined,
        amenities: values.amenities
          ? values.amenities
              .split(',')
              .map((a) => a.trim())
              .filter(Boolean)
          : []
      };
      const res = editing
        ? await editRoom({ id: editing._id, data: payload })
        : await addRoom(payload);

      if (res?.data?.success) {
        notification(editing ? 'Room updated' : 'Room added', TOAST_SUCCESS);
        setShowForm(false);
        reset(emptyRoom);
        refetch();
      } else {
        notification(res?.error?.data?.statusMessage || 'Could not save room', TOAST_ERROR);
      }
    } catch (error) {
      notification(error?.message, TOAST_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const toggleMaintenance = async (room) => {
    const nextStatus =
      room.status === ROOM_STATUS.MAINTENANCE ? ROOM_STATUS.AVAILABLE : ROOM_STATUS.MAINTENANCE;
    try {
      const res = await editRoom({ id: room._id, data: { status: nextStatus } });
      if (res?.data?.success) {
        notification(
          nextStatus === ROOM_STATUS.MAINTENANCE
            ? `${room.name} marked under maintenance`
            : `${room.name} is available again`,
          TOAST_SUCCESS
        );
        refetch();
      } else {
        notification(res?.error?.data?.statusMessage || 'Could not update', TOAST_ERROR);
      }
    } catch (error) {
      notification(error?.message, TOAST_ERROR);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const res = await deleteRoom(deleteModal.id);
      if (res?.data?.success) {
        notification('Room deleted', TOAST_SUCCESS);
        refetch();
      } else {
        notification(res?.error?.data?.statusMessage || 'Could not delete', TOAST_ERROR);
      }
    } catch (error) {
      notification(error?.message, TOAST_ERROR);
    } finally {
      setDeleting(false);
      setDeleteModal({ show: false, id: '' });
    }
  };

  const handleClose = () => {
    setShowForm(false);
    onHide();
  };

  const formTitle = editing ? 'Edit room' : 'Add room';

  return (
    <>
      <Modal
        centered
        className="rb-modal"
        dialogClassName="rb-modal__dialog rb-modal__dialog--wide"
        contentClassName="rb-modal__content"
        show={show}
        onHide={handleClose}
      >
        <Modal.Header className="rb-modal__header">
          <div className="rb-modal__header-content">
            <span className="rb-modal__header-icon" aria-hidden="true">
              <i className={showForm ? 'icofont-ui-edit' : 'icofont-gear'} />
            </span>
            <div className="rb-modal__header-copy">
              <Modal.Title className="rb-modal__title">
                {showForm ? formTitle : 'Manage conference rooms'}
              </Modal.Title>
              <p className="rb-modal__subtitle">
                {showForm
                  ? editing
                    ? 'Update this room name, seats, and colour'
                    : 'Add a room people can book on the calendar'
                  : 'Add rooms, or mark one under maintenance'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close rb-modal__close"
            aria-label="Close"
            onClick={handleClose}
          />
        </Modal.Header>

        {!showForm ? (
          <Modal.Body className="rb-modal__body">
            <div className="rb-rooms-toolbar">
              <span className="rb-rooms-toolbar__count">
                {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'}
              </span>
              <button type="button" className="btn room-booking__add-btn" onClick={openNew}>
                <i className="icofont-plus-circle" aria-hidden="true" />
                Add room
              </button>
            </div>

            <div className="rb-rooms">
              {rooms.length === 0 ? (
                <div className="rb-rooms__empty">No rooms yet. Add your first room.</div>
              ) : (
                rooms.map((room) => {
                  const inMaintenance = room.status === ROOM_STATUS.MAINTENANCE;
                  return (
                    <div key={room._id} className="rb-room-card">
                      <span className="rb-room-card__color" style={{ background: room.color }} />
                      <div className="rb-room-card__info">
                        <div className="rb-room-card__name">
                          {room.name}
                          {inMaintenance && (
                            <span className="rb-room-card__badge">
                              <i className="icofont-tools-alt-2" /> Maintenance
                            </span>
                          )}
                        </div>
                        <div className="rb-room-card__meta">
                          <span>
                            <i className="icofont-users-alt-3" /> {room.capacity} seats
                          </span>
                          {room.location ? (
                            <span>
                              <i className="icofont-location-pin" /> {room.location}
                            </span>
                          ) : null}
                          {room.amenities?.length ? (
                            <span>
                              <i className="icofont-star" /> {room.amenities.join(', ')}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="rb-room-card__actions">
                        <button
                          type="button"
                          className={`rb-icon-btn ${inMaintenance ? 'is-warn' : ''}`}
                          title={inMaintenance ? 'Mark available' : 'Set under maintenance'}
                          onClick={() => toggleMaintenance(room)}
                        >
                          <i
                            className={
                              inMaintenance ? 'icofont-check-circled' : 'icofont-tools-alt-2'
                            }
                          />
                        </button>
                        <button
                          type="button"
                          className="rb-icon-btn"
                          title="Edit"
                          onClick={() => openEdit(room)}
                        >
                          <i className="icofont-ui-edit" />
                        </button>
                        <button
                          type="button"
                          className="rb-icon-btn is-danger"
                          title="Delete"
                          onClick={() => setDeleteModal({ show: true, id: room._id })}
                        >
                          <i className="icofont-ui-delete" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Modal.Body>
        ) : (
          <form className="rb-modal__form" onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body className="rb-modal__body">
              <div className="rb-modal__section">
                <div className="rb-modal__field">
                  <label className="rb-modal__label" htmlFor="rb-room-name">
                    Room name *
                  </label>
                  <input
                    id="rb-room-name"
                    className="form-control rb-modal__input"
                    placeholder="e.g. Sydney"
                    {...register('name', { required: 'Please enter a name' })}
                  />
                  {errors.name && (
                    <small className="text-danger rb-modal__error">{errors.name.message}</small>
                  )}
                </div>

                <div className="rb-modal__grid">
                  <div className="rb-modal__field">
                    <label className="rb-modal__label" htmlFor="rb-capacity">
                      Capacity *
                    </label>
                    <input
                      id="rb-capacity"
                      type="number"
                      min="1"
                      className="form-control rb-modal__input"
                      placeholder="Seats"
                      {...register('capacity', {
                        required: 'Required',
                        min: { value: 1, message: 'Min 1' }
                      })}
                    />
                    {errors.capacity && (
                      <small className="text-danger rb-modal__error">
                        {errors.capacity.message}
                      </small>
                    )}
                  </div>
                  <div className="rb-modal__field">
                    <label className="rb-modal__label" htmlFor="rb-location">
                      Location
                    </label>
                    <input
                      id="rb-location"
                      className="form-control rb-modal__input"
                      placeholder="e.g. Floor 1"
                      {...register('location')}
                    />
                  </div>
                </div>

                <div className="rb-modal__field">
                  <label className="rb-modal__label" htmlFor="rb-amenities">
                    Amenities
                  </label>
                  <input
                    id="rb-amenities"
                    className="form-control rb-modal__input"
                    placeholder="Comma separated, e.g. TV, Whiteboard"
                    {...register('amenities')}
                  />
                </div>

                <div className="rb-modal__field">
                  <label className="rb-modal__label">Calendar colour</label>
                  <div className="rb-color-picker">
                    <label
                      className="rb-color-picker__native"
                      style={{ background: selectedColor }}
                    >
                      <input
                        type="color"
                        value={selectedColor || '#484c7f'}
                        onChange={(e) => setValue('color', e.target.value)}
                      />
                      <i className="icofont-color-picker" aria-hidden="true" />
                    </label>
                    <div className="rb-swatches">
                      {COLOR_SWATCHES.map((c) => (
                        <button
                          type="button"
                          key={c}
                          className={`rb-swatch ${selectedColor?.toLowerCase() === c.toLowerCase() ? 'is-active' : ''}`}
                          style={{ background: c }}
                          onClick={() => setValue('color', c)}
                          aria-label={`Use colour ${c}`}
                        />
                      ))}
                    </div>
                    <span className="rb-color-picker__value">{selectedColor}</span>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="rb-modal__footer">
              <button
                type="button"
                className="btn rb-modal__cancel"
                onClick={() => setShowForm(false)}
              >
                Back
              </button>
              <button type="submit" className="btn rb-modal__submit" disabled={saving}>
                {saving ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  <>
                    <i className="icofont-check-circled" aria-hidden="true" />
                    {editing ? 'Save changes' : 'Add room'}
                  </>
                )}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, id: '' })}
        onConfirm={handleDelete}
        loading={deleting}
        variant="danger"
        icon="icofont-ui-delete"
        title="Delete room?"
        message="Deleting a room does not remove its past bookings. Are you sure?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default ManageRoomsModal;
